import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useFamily } from "@/hooks/useFamily";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getDateString } from "@/lib/family-date";
import { logActivity } from "@/lib/activity-log";
import type { Reminder } from "@/hooks/useReminders";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reminder: Reminder | null;
}

/** MVP: Only message / bring / deadline. Legacy action/event are shown as Beskjed and cannot be re-selected. */
const CATEGORIES = [
  { value: "info", label: "Beskjed", emoji: "💬" },
  { value: "take_along", label: "Ta med", emoji: "🎒" },
  { value: "deadline", label: "Frist", emoji: "⏰" },
];

export function EditReminderSheet({ open, onOpenChange, reminder }: Props) {
  const { timezone } = useFamily();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("info");
  const [hasDate, setHasDate] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [hasTime, setHasTime] = useState(false);
  const [timeValue, setTimeValue] = useState("08:00");

  // Populate form when reminder changes. Legacy: action/event → show as Beskjed (info).
  useEffect(() => {
    if (!reminder) return;
    setTitle(reminder.title);
    const raw = reminder.category || "info";
    setCategory(raw === "action" || raw === "event" ? "info" : raw);
    if (reminder.remind_date) {
      setHasDate(true);
      setSelectedDate(new Date(reminder.remind_date + "T12:00:00"));
    } else {
      setHasDate(false);
      setSelectedDate(undefined);
    }
    if (reminder.remind_at) {
      setHasTime(true);
      const timePart = reminder.remind_at.includes("T")
        ? reminder.remind_at.split("T")[1]?.slice(0, 5)
        : "08:00";
      setTimeValue(timePart);
    } else {
      setHasTime(false);
      setTimeValue("08:00");
    }
  }, [reminder]);

  async function handleSave() {
    if (!reminder || !title.trim()) return;
    setSaving(true);
    try {
      const remindDate = hasDate && selectedDate
        ? getDateString(selectedDate, timezone)
        : null;

      let remindAt: string | null = null;
      if (hasTime && remindDate) {
        remindAt = `${remindDate}T${timeValue}:00`;
      }

      const updates: Record<string, unknown> = {
        title: title.trim(),
        category,
        remind_date: remindDate,
        remind_at: remindAt,
      };

      // Track changes for audit log
      const changes: Record<string, { old: unknown; new: unknown }> = {};
      if (reminder.title !== updates.title) changes.title = { old: reminder.title, new: updates.title };
      if (reminder.category !== updates.category) changes.category = { old: reminder.category, new: updates.category };
      if (reminder.remind_date !== updates.remind_date) changes.remind_date = { old: reminder.remind_date, new: updates.remind_date };
      if (reminder.remind_at !== updates.remind_at) changes.remind_at = { old: reminder.remind_at, new: updates.remind_at };

      const { error } = await supabase
        .from("reminders")
        .update(updates)
        .eq("id", reminder.id);

      if (error) throw error;

      if (Object.keys(changes).length > 0) {
        logActivity({
          familyId: reminder.family_id,
          childId: reminder.child_id,
          action: "updated",
          entityType: "reminder",
          entityId: reminder.id,
          changes,
        });
      }

      toast.success("Påminnelse oppdatert");
      queryClient.invalidateQueries({ queryKey: ["reminders", reminder.child_id] });
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Kunne ikke oppdatere påminnelse");
    } finally {
      setSaving(false);
    }
  }

  const selectedCategoryEmoji = CATEGORIES.find((c) => c.value === category)?.emoji || "📋";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-lg font-display">Rediger påminnelse</SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Endre påminnelsen
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 mt-4 pb-4">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="edit-reminder-title">Tittel</Label>
            <div className="flex items-center gap-2">
              <span className="text-xl">{selectedCategoryEmoji}</span>
              <Input
                id="edit-reminder-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="F.eks. Ta med medisin"
                className="flex-1"
                
              />
            </div>
          </div>

          {/* Category */}
          <div className="space-y-1.5">
            <Label>Kategori</Label>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((cat) => (
                <Badge
                  key={cat.value}
                  variant={category === cat.value ? "default" : "outline"}
                  className={cn(
                    "cursor-pointer text-xs px-2.5 py-1 transition-colors",
                    category === cat.value
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted"
                  )}
                  onClick={() => setCategory(cat.value)}
                >
                  {cat.emoji} {cat.label}
                </Badge>
              ))}
            </div>
          </div>

          {/* Date toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm">Dato</Label>
              </div>
              <Switch checked={hasDate} onCheckedChange={setHasDate} />
            </div>

            {hasDate && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="w-full justify-start text-left font-normal gap-2">
                    <CalendarDays className="h-4 w-4" />
                    {selectedDate
                      ? selectedDate.toLocaleDateString("no-NO", { weekday: "long", day: "numeric", month: "long" })
                      : "Velg dato"
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Time toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm">Klokkeslett</Label>
              </div>
              <Switch checked={hasTime} onCheckedChange={setHasTime} />
            </div>

            {hasTime && (
              <Input
                type="time"
                value={timeValue}
                onChange={(e) => setTimeValue(e.target.value)}
                className="w-32"
              />
            )}
          </div>

          {/* Save */}
          <Button
            onClick={handleSave}
            disabled={saving || !title.trim()}
            className="w-full rounded-xl"
          >
            {saving ? "Lagrer…" : "Lagre endringer"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
